import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { LoadingService } from "../../services/loading.service";
import { SocialSignInService } from "../../services/social-sign-in.service";

/**
 * The last leg of the contract-email flow.
 *
 * The backend has built the account and attached the Google account to it, and
 * sends the browser here. Only Cognito can create a session, so one more trip
 * to Google cannot be avoided. This page announces the trip and starts it,
 * instead of sending the customer back to the login screen to find the Google
 * button a second time.
 *
 * Nobody is signed in when this page loads - the account exists, the session
 * does not - so the route carries no guard.
 */
@Component({
  selector: 'cpf-link-complete-page',
  templateUrl: './link-complete-page.component.html',
  styleUrls: ['./link-complete-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LinkCompletePageComponent implements OnInit {
  /** The address the backend linked. It is shown, and nothing else. */
  private static readonly EMAIL_PARAM = 'googleLoginEmail';
  /**
   * How long the message stands before the browser leaves for Google.
   *
   * Without it the trip starts on the same frame the message is painted, and
   * the customer arrives at Google without ever having read why.
   */
  private static readonly MESSAGE_PAUSE_MS = 2500;

  email: string | null = null;
  /** Amplify would not start the trip. The customer needs a way out. */
  failed = false;

  constructor(private route: ActivatedRoute,
              private socialSignInService: SocialSignInService,
              private loadingService: LoadingService) {
  }

  async ngOnInit(): Promise<void> {
    this.email = this.route.snapshot.queryParamMap.get(LinkCompletePageComponent.EMAIL_PARAM);

    // This page is its own message, and nothing else has to load before it can
    // be read. Without this the bootstrap loading screen covers it until the
    // session check finishes, and the customer sees nothing before Google.
    this.loadingService.bootstrapComplete();

    await this.pause(LinkCompletePageComponent.MESSAGE_PAUSE_MS);

    try {
      await this.socialSignInService.signInWithGoogle();
    } catch (error) {
      // The console line names the refusal, which is what makes a support
      // ticket answerable. The screen says only that it did not work.
      console.error('Could not start the Google sign-in', error);
      this.failed = true;
    }
  }

  /** Isolated so a test does not have to wait it out. */
  private pause(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
